import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import FormError from '../../components/shared/FormError';
import Tooltip from '../../components/shared/Tooltip';
import api from '../../services/api';
import { autoCapFirst } from '../../utils/strings';

interface DealerGroup {
  id: string;
  name: string;
  oemBrand: string | null;
  region: string | null;
  isActive: boolean;
  createdAt: string;
}

const groupSchema = z.object({
  name: z.string().min(1, 'Group name is required'),
  oemBrand: z.string().optional(),
  region: z.string().optional(),
});
type GroupForm = z.infer<typeof groupSchema>;

const DealerGroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<DealerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState<DealerGroup | null>(null);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue: setGroupValue,
    formState: { errors },
  } = useForm<GroupForm>({
    resolver: zodResolver(groupSchema),
    defaultValues: { name: '', oemBrand: '', region: '' },
  });

  // Group admin users panel
  const [usersGroup, setUsersGroup] = useState<DealerGroup | null>(null);
  const [groupUsers, setGroupUsers] = useState<{ id: string; firstName: string | null; lastName: string | null; email: string; isActive: boolean }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [userForm, setUserForm] = useState({ firstName: '', lastName: '', email: '' });
  const [userFormError, setUserFormError] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/dealer-groups');
      setGroups(res.data.data ?? []);
    } catch {
      showToast('Failed to load dealer groups.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const resetForm = () => {
    reset({ name: '', oemBrand: '', region: '' });
    setShowCreate(false);
    setEditGroup(null);
  };

  const openEdit = (group: DealerGroup) => {
    setEditGroup(group);
    reset({ name: group.name, oemBrand: group.oemBrand ?? '', region: group.region ?? '' });
    setShowCreate(false);
  };

  const onSubmit = async (data: GroupForm) => {
    setSaving(true);
    try {
      if (editGroup) {
        const res = await api.patch(`/admin/dealer-groups/${editGroup.id}`, {
          name: data.name.trim(),
          oemBrand: data.oemBrand?.trim() || undefined,
          region: data.region?.trim() || undefined,
        });
        setGroups((prev) => prev.map((g) => g.id === editGroup.id ? res.data.data : g));
        showToast('Dealer group updated.', 'success');
      } else {
        const res = await api.post('/admin/dealer-groups', {
          name: data.name.trim(),
          oemBrand: data.oemBrand?.trim() || undefined,
          region: data.region?.trim() || undefined,
        });
        setGroups((prev) => [...prev, res.data.data as DealerGroup]);
        showToast('Dealer group created.', 'success');
      }
      resetForm();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showToast(msg ?? 'Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (group: DealerGroup) => {
    try {
      const res = await api.patch(`/admin/dealer-groups/${group.id}`, { isActive: !group.isActive });
      setGroups((prev) => prev.map((g) => g.id === group.id ? res.data.data : g));
      showToast(`${group.name} ${!group.isActive ? 'activated' : 'deactivated'}.`, 'success');
    } catch {
      showToast('Failed to update.', 'error');
    }
  };

  const openGroupUsers = useCallback(async (group: DealerGroup) => {
    if (usersGroup?.id === group.id) {
      setUsersGroup(null);
      setGroupUsers([]);
      setShowAddUser(false);
      setUserFormError(null);
      return;
    }
    setUsersGroup(group);
    setShowAddUser(false);
    setUserFormError(null);
    setUsersLoading(true);
    try {
      const res = await api.get(`/admin/dealer-groups/${group.id}/users`);
      setGroupUsers(res.data.data ?? []);
    } catch {
      showToast('Failed to load group admin users.', 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [usersGroup, showToast]);

  const handleAddUser = async () => {
    if (!usersGroup) return;
    setUserFormError(null);
    if (!userForm.firstName.trim() || !userForm.lastName.trim() || !userForm.email.trim()) {
      setUserFormError('All fields are required.');
      return;
    }
    setAddingUser(true);
    try {
      const res = await api.post(`/admin/dealer-groups/${usersGroup.id}/users`, {
        firstName: userForm.firstName.trim(),
        lastName: userForm.lastName.trim(),
        email: userForm.email.trim(),
      });
      setGroupUsers((prev) => [res.data.data, ...prev]);
      setUserForm({ firstName: '', lastName: '', email: '' });
      setShowAddUser(false);
      showToast('Group admin created and invited via email.', 'success');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: { message: string }[] } } })?.response?.data?.errors?.[0]?.message;
      setUserFormError(msg ?? 'Failed to create user.');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDelete = async (group: DealerGroup) => {
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/dealer-groups/${group.id}`);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      showToast('Dealer group deleted.', 'success');
    } catch {
      showToast('Failed to delete.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">Dealer Groups</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              Manage OEM brand groups and regional dealer clusters
            </p>
          </div>
          <button
            type="button"
            onClick={() => { resetForm(); setShowCreate(true); }}
            className="h-12 px-8 rounded-2xl bg-primary text-white font-bold text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            New Group
          </button>
        </div>

        {/* Create / Edit Form */}
        {(showCreate || editGroup) && (
          <form onSubmit={handleSubmit(onSubmit)} className="mb-8 bg-white dark:bg-slate-900 border border-primary/20 p-8 space-y-6">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              {editGroup ? 'Edit Dealer Group' : 'Create Dealer Group'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('name')}
                  aria-invalid={!!errors.name}
                  onChange={(e) => setGroupValue('name', autoCapFirst(e.target.value), { shouldDirty: true })}
                  placeholder="e.g. Pacific Northwest Ford Group"
                  className={`h-12 w-full border-b bg-transparent px-1 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:outline-none transition-colors ${
                    errors.name
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-slate-200 dark:border-slate-700 focus:border-primary'
                  }`}
                />
                <FormError message={errors.name?.message} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">OEM Brand</label>
                <input
                  {...register('oemBrand')}
                  onChange={(e) => setGroupValue('oemBrand', autoCapFirst(e.target.value), { shouldDirty: true })}
                  placeholder="e.g. Ford"
                  className="h-12 w-full border-b border-slate-200 dark:border-slate-700 bg-transparent px-1 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:border-primary focus:outline-none transition-colors"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Region</label>
                <input
                  {...register('region')}
                  onChange={(e) => setGroupValue('region', autoCapFirst(e.target.value), { shouldDirty: true })}
                  placeholder="e.g. Pacific Northwest"
                  className="h-12 w-full border-b border-slate-200 dark:border-slate-700 bg-transparent px-1 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:border-primary focus:outline-none transition-colors"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={resetForm}
                className="h-10 px-6 font-bold text-xs uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="h-10 px-6 font-bold text-xs uppercase tracking-widest bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                ) : (
                  <><span className="material-symbols-outlined text-[16px]">save</span>{editGroup ? 'Update' : 'Create'}</>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Groups Table */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
              <span className="text-sm font-semibold text-slate-400 dark:text-slate-600 uppercase tracking-widest">Loading groups...</span>
            </div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">corporate_fare</span>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-base">No dealer groups yet.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  {['Name', 'OEM Brand', 'Region', 'Status', 'Created', 'Actions'].map((h) => (
                    <th key={h} className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                    <td className="px-6 py-4 font-bold text-slate-900 dark:text-white text-sm">{group.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">{group.oemBrand ?? '—'}</td>
                    <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">{group.region ?? '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        group.isActive
                          ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${group.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        {group.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400 dark:text-slate-600">
                      {new Date(group.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Tooltip content="Edit">
                          <button
                            type="button"
                            onClick={() => openEdit(group)}
                            className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary hover:bg-primary/10 transition-all"
                          >
                            <span className="material-symbols-outlined text-[18px]">edit</span>
                          </button>
                        </Tooltip>
                        <Tooltip content="Group Admins">
                          <button
                            type="button"
                            onClick={() => openGroupUsers(group)}
                            className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all ${
                              usersGroup?.id === group.id
                                ? 'bg-violet-100 dark:bg-violet-950/40 text-violet-500'
                                : 'text-slate-400 hover:text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-950/30'
                            }`}
                          >
                            <span className="material-symbols-outlined text-[18px]">manage_accounts</span>
                          </button>
                        </Tooltip>
                        <Tooltip content={group.isActive ? 'Deactivate' : 'Activate'}>
                          <button
                            type="button"
                            onClick={() => handleToggleActive(group)}
                            className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all"
                          >
                            <span className="material-symbols-outlined text-[18px]">{group.isActive ? 'toggle_on' : 'toggle_off'}</span>
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete">
                          <button
                            type="button"
                            onClick={() => handleDelete(group)}
                            className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {/* Group Admin Users Panel */}
        {usersGroup && (
          <div className="mt-8 bg-white dark:bg-slate-900 border border-violet-200 dark:border-violet-900/40 rounded-2xl p-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-950/30 flex items-center justify-center text-violet-500">
                  <span className="material-symbols-outlined text-[20px]">manage_accounts</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white text-base">
                    Group Admins — <span className="text-violet-500">{usersGroup.name}</span>
                  </h3>
                  <p className="text-[11px] text-slate-400 dark:text-slate-600 mt-0.5">
                    Users with GROUP_ADMIN access to this dealer group.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setShowAddUser(true); setUserFormError(null); }}
                className="h-9 px-5 rounded-xl bg-violet-500 text-white font-bold text-[10px] uppercase tracking-widest hover:bg-violet-600 transition-all flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[16px]">person_add</span>
                Add Admin
              </button>
            </div>

            {showAddUser && (
              <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">New Group Admin</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(['firstName', 'lastName', 'email'] as const).map((field) => (
                    <input
                      key={field}
                      type={field === 'email' ? 'email' : 'text'}
                      placeholder={field === 'firstName' ? 'First name' : field === 'lastName' ? 'Last name' : 'Email address'}
                      value={userForm[field]}
                      onChange={(e) => setUserForm((prev) => ({ ...prev, [field]: e.target.value }))}
                      className="h-10 w-full border-b border-slate-300 dark:border-slate-600 bg-transparent px-1 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-violet-500 focus:outline-none transition-colors"
                    />
                  ))}
                </div>
                {userFormError && (
                  <p className="text-[11px] font-semibold text-red-500">{userFormError}</p>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowAddUser(false); setUserFormError(null); }}
                    className="h-9 px-5 font-bold text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-all rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddUser}
                    disabled={addingUser}
                    className="h-9 px-5 font-bold text-[10px] uppercase tracking-widest bg-violet-500 text-white hover:bg-violet-600 transition-all rounded-lg disabled:opacity-50 flex items-center gap-2"
                  >
                    {addingUser ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white animate-spin rounded-full" /> : null}
                    Create & Invite
                  </button>
                </div>
              </div>
            )}

            {usersLoading ? (
              <div className="flex items-center justify-center py-8 gap-3">
                <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent animate-spin rounded-full" />
                <span className="text-sm text-slate-400">Loading users...</span>
              </div>
            ) : groupUsers.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No group admins yet. Add one above.</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    {['Name', 'Email', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupUsers.map((u) => (
                    <tr key={u.id} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white text-sm">{u.firstName} {u.lastName}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                          u.isActive
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

      </main>
    </div>
  );
};

export default DealerGroupsPage;
